import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useCancelWorkflowExecutionAction, type PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import { formatError } from "@/utils/errors";
import { type FC } from "react";
import { useCallbackPreventDefault } from "@/hooks/use-callback-prevent-default";
import { Card } from "@/components/flexkit/Card";
import { Text } from "@/components/podkit/typography/Text";
import { WorkflowExecutionActionStatusPill } from "@/components/automations/AutomationExecutionActionStatusPill";

type CancelWorkflowExecutionActionDialogProps = {
    action: PlainWorkflowExecutionAction;
    open: boolean;
    onClose: () => void;
};

export const CancelWorkflowExecutionActionDialog: FC<CancelWorkflowExecutionActionDialogProps> = ({
    action,
    open,
    onClose,
}) => {
    const { toast } = useToast();
    const cancelWorkflowExecutionAction = useCancelWorkflowExecutionAction();

    const handleCancel = useCallbackPreventDefault(async () => {
        try {
            await cancelWorkflowExecutionAction.mutateAsync(action);
            onClose();
        } catch (error) {
            toast({
                title: "Failed to cancel workflow execution action",
                description: formatError(error),
            });
        }
    }, [cancelWorkflowExecutionAction, action, toast, onClose]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="cancel-workflow-execution-action-dialog"
                data-track-location={TrackLocations.CancelWorkflowExecutionActionDialog}
            >
                <DialogHeader>
                    <DialogTitle>Cancel Workflow Execution Action</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to cancel this workflow execution action? Running steps will be stopped.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <Card variant="bordered" className="border bg-surface-secondary shadow-sm">
                        <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1">
                                <Text className="text-base font-medium text-content-primary">{action.id}</Text>
                                <WorkflowExecutionActionStatusPill action={action} />
                            </div>
                        </div>
                    </Card>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="cancel-workflow-execution-action-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleCancel}
                        loading={cancelWorkflowExecutionAction.isPending}
                        data-testid="cancel-workflow-execution-action-dialog-confirm"
                        data-tracking-id="confirm-cancel-workflow-execution-action-cancel-workflow-execution-action-dialog"
                    >
                        Cancel Action
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
